/**
 * interceptors.ts - Axios 인터셉터 설정
 * 
 * 모든 API 요청/응답에 대한 공통 처리를 담당합니다.
 * - 토큰 자동 첨부
 * - 토큰 갱신
 * - 에러 처리
 * - 로깅
 */

import { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiErrorResponse } from './axiosConfig';

// 토큰 관리 인터페이스
interface TokenManager {
  getAccessToken: () => string | null;
  getRefreshToken: () => string | null;
  setTokens: (accessToken: string, refreshToken: string) => void;
  clearTokens: () => void;
  refreshAccessToken: () => Promise<string>;
}

// 로깅 설정
interface LogConfig {
  enabled: boolean;
  logRequest: boolean;
  logResponse: boolean;
  logError: boolean;
}

// 인터셉터 설정 옵션
export interface InterceptorOptions {
  tokenManager?: TokenManager;
  logConfig?: LogConfig;
  onUnauthorized?: () => void;
  onNetworkError?: (error: AxiosError) => void;
}

// 기본 로깅 설정
const defaultLogConfig: LogConfig = {
  enabled: process.env.NODE_ENV === 'development',
  logRequest: true,
  logResponse: true,
  logError: true,
};

// 요청 인터셉터 설정
export const setupRequestInterceptor = (
  axiosInstance: AxiosInstance,
  options: InterceptorOptions = {}
): void => {
  const { tokenManager, logConfig = defaultLogConfig } = options;

  axiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // 토큰 첨부
      if (tokenManager) {
        const token = tokenManager.getAccessToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      // 요청 로깅
      if (logConfig.enabled && logConfig.logRequest) {
        console.log(
          `🌐 [API Request] ${config.method?.toUpperCase()} ${config.url}`,
          {
            headers: config.headers,
            data: config.data,
            params: config.params,
          }
        );
      }

      // 요청 시간 기록 (응답 시간 계산용)
      config.metadata = { ...config.metadata, requestTime: Date.now() };

      return config;
    },
    (error: AxiosError) => {
      if (logConfig.enabled && logConfig.logError) {
        console.error('🚨 [API Request Error]', error);
      }
      return Promise.reject(error);
    }
  );
};

// 응답 인터셉터 설정
export const setupResponseInterceptor = (
  axiosInstance: AxiosInstance,
  options: InterceptorOptions = {}
): void => {
  const { tokenManager, logConfig = defaultLogConfig, onUnauthorized, onNetworkError } = options;

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      // 응답 시간 계산
      const requestTime = response.config.metadata?.requestTime;
      const responseTime = requestTime ? Date.now() - requestTime : null;

      // 응답 로깅
      if (logConfig.enabled && logConfig.logResponse) {
        console.log(
          `✅ [API Response] ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`,
          {
            data: response.data,
            responseTime: responseTime ? `${responseTime}ms` : 'N/A',
          }
        );
      }

      return response;
    },
    async (error: AxiosError<ApiErrorResponse>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // 에러 로깅
      if (logConfig.enabled && logConfig.logError) {
        console.error(
          `❌ [API Error] ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url} - ${error.response?.status}`,
          {
            message: error.message,
            response: error.response?.data,
            stack: error.stack,
          }
        );
      }

      // 네트워크 에러 처리
      if (!error.response) {
        if (onNetworkError) {
          onNetworkError(error);
        }
        return Promise.reject(error);
      }

      // 401 에러 처리 (토큰 만료)
      if (error.response.status === 401 && !originalRequest._retry && tokenManager) {
        originalRequest._retry = true;

        try {
          // 리프레시 토큰으로 새 액세스 토큰 요청
          const newAccessToken = await tokenManager.refreshAccessToken();
          
          // 새 토큰으로 원래 요청 재시도
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          }
          
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          // 🔴 서버가 열쇠를 거절했을 때만 로그아웃한다 (2026-09-24).
          //    종전엔 네트워크 오류·타임아웃·5xx 한 번에도 로그인 화면으로 튕겼다.
          //    authFailure 표시가 없으면 이번 요청만 실패시키고 로그인은 유지한다.
          if ((refreshError as any)?.authFailure) {
            if (onUnauthorized) {
              onUnauthorized();
            }
            tokenManager.clearTokens();
          }
          return Promise.reject(refreshError);
        }
      }

      // 403 에러 처리 (권한 없음)
      if (error.response.status === 403 && onUnauthorized) {
        onUnauthorized();
      }

      return Promise.reject(error);
    }
  );
};

// 모든 인터셉터 설정
export const setupInterceptors = (
  axiosInstance: AxiosInstance,
  options: InterceptorOptions = {}
): void => {
  setupRequestInterceptor(axiosInstance, options);
  setupResponseInterceptor(axiosInstance, options);
};

// 인터셉터 제거
export const removeInterceptors = (axiosInstance: AxiosInstance): void => {
  axiosInstance.interceptors.request.clear();
  axiosInstance.interceptors.response.clear();
};

// Axios 인스턴스 메타데이터 타입 확장
declare module 'axios' {
  export interface AxiosRequestConfig {
    metadata?: {
      requestTime?: number;
      [key: string]: any;
    };
  }
}